import type { PaginatedQuery } from '@atlas/api-contract';

/**
 * URL search state for a paginated list, and the rules for reading it back.
 *
 * The URL is the source of truth for search, sort and page (spec R6.5): a
 * reload, a bookmark or a link pasted into a chat has to restore the same view.
 * That only holds if parsing is total — every value in a URL is a string
 * somebody could have typed, so nothing here throws; an unusable value falls
 * back to its default exactly as the API does with an unknown `sort`.
 *
 * Defaults are *omitted* rather than written out, so the default view is a bare
 * `/` and a shared link carries only what actually differs. The same function
 * normalises updates, which is what keeps `?page=1` from ever appearing after
 * paging back to the first page.
 *
 * Written once here because every list in Atlas — projects, then prototypes —
 * has the same three controls.
 */

/** The URL-facing shape. Every key is absent when it holds its default. */
export interface ListSearch<TSort extends string> {
  /** Free-text filter. Absent when empty. */
  search?: string;
  /** `field:direction` sort key. Absent when it is the default. */
  sort?: TSort;
  /** 1-based page. Absent when it is page 1. */
  page?: number;
}

/** The same state with every default applied, for components and queries. */
export interface ResolvedListSearch<TSort extends string> {
  search: string;
  sort: TSort;
  page: number;
  pageSize: number;
}

export interface ListSearchConfig<TSort extends string> {
  /** Every sort key the endpoint accepts. Anything else is discarded. */
  readonly sorts: readonly TSort[];
  /** The sort applied when the URL names none. */
  readonly defaultSort: TSort;
  /** Items per page. Not URL-controlled: it is a layout decision, not a view. */
  readonly pageSize: number;
}

export interface ListSearchCodec<TSort extends string> {
  /** Normalise raw URL search params, or an updated search object. */
  parse: (raw: Record<string, unknown>) => ListSearch<TSort>;
  /** Apply the defaults the URL leaves out. */
  resolve: (search: ListSearch<TSort>) => ResolvedListSearch<TSort>;
  /** Build the query the API expects, omitting an empty search term. */
  toQuery: (search: ListSearch<TSort>) => PaginatedQuery;
}

/** The first page. Never written to the URL. */
export const FIRST_PAGE = 1;

/**
 * Read a page number from a URL value.
 *
 * Query strings arrive as strings, but a caller updating the search object
 * passes a number, so both are accepted. Anything fractional, negative, absent
 * or nonsense is page 1 — a list with no rows is a worse answer than the first
 * page.
 */
const parsePage = (value: unknown): number => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : Number.NaN;

  if (!Number.isFinite(parsed)) {
    return FIRST_PAGE;
  }

  return Math.max(FIRST_PAGE, Math.floor(parsed));
};

/** Read and trim a search term. Non-strings are treated as absent. */
const parseSearch = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const createListSearch = <TSort extends string>(
  config: ListSearchConfig<TSort>,
): ListSearchCodec<TSort> => {
  const isSort = (value: unknown): value is TSort =>
    typeof value === 'string' && (config.sorts as readonly string[]).includes(value);

  const parse = (raw: Record<string, unknown>): ListSearch<TSort> => {
    const search = parseSearch(raw['search']);
    const sort = raw['sort'];
    const page = parsePage(raw['page']);

    return {
      ...(search === '' ? {} : { search }),
      ...(isSort(sort) && sort !== config.defaultSort ? { sort } : {}),
      ...(page === FIRST_PAGE ? {} : { page }),
    };
  };

  const resolve = (search: ListSearch<TSort>): ResolvedListSearch<TSort> => ({
    search: search.search ?? '',
    sort: search.sort ?? config.defaultSort,
    page: search.page ?? FIRST_PAGE,
    pageSize: config.pageSize,
  });

  const toQuery = (search: ListSearch<TSort>): PaginatedQuery => {
    const resolved = resolve(search);

    return {
      ...(resolved.search === '' ? {} : { search: resolved.search }),
      sort: resolved.sort,
      page: resolved.page,
      pageSize: resolved.pageSize,
    };
  };

  return { parse, resolve, toQuery };
};
